import type { SrSalesOrderType } from '@plato/data/starrocks/types';
import { BillingStatus } from '../server/utils/explorer-utils.ts';

/** The namespace holding every calculation explainer (REQ-3777). */
export const metricDefinitionsNamespace = 'metric-definitions';

export type ExplainableMetric = BillingStatus | 'GROSS_MARGIN' | 'GROSS_MARGIN_PERCENTAGE' | 'UNITS';

export const explainableMetrics: ExplainableMetric[] = [
    ...Object.values(BillingStatus),
    'GROSS_MARGIN',
    'GROSS_MARGIN_PERCENTAGE',
    'UNITS',
];

/** What a margin figure is calculated on. `ORDER_VALUE` and `QUOTE_VALUE` are one document's lines. */
export type MetricBase = BillingStatus | 'ORDER_VALUE' | 'QUOTE_VALUE';

export const metricBases: MetricBase[] = [...Object.values(BillingStatus), 'ORDER_VALUE', 'QUOTE_VALUE'];

/** Interpolated as `base`. The phrases carry their own article and case. */
export function metricBaseKey(base: MetricBase): string {
    switch (base) {
        case BillingStatus.BILLED:
            return 'base__billed';
        case BillingStatus.BILLED_WO_CREDIT_NOTE:
            return 'base__billed_wo_credit_note';
        case BillingStatus.NOT_BILLED:
            return 'base__not_billed';
        case BillingStatus.ORDER_SUM:
            return 'base__order_sum';
        case 'ORDER_VALUE':
            return 'base__order_value';
        case 'QUOTE_VALUE':
            return 'base__quote_value';
    }
}

/** Stands in where no base is known, as in the FAQ's list of every figure. */
export const selectedFigureBaseKey = 'base__selected_figure';

type MetricDefinitionKeys = {
    label: string;
    definition: string;
    /** Names the tenant's ERP document types, interpolating `documentTypes`. Only `ORDER_SUM` has one. */
    documentTypes?: string;
    /** Which date the figure is booked on. Only the four value metrics have one. */
    dateBasis?: string;
};

/**
 * i18n keys for one figure's explainer. Every key is spelled out because
 * translation keys must never be assembled from values at runtime.
 */
export function metricDefinitionKeys(metric: ExplainableMetric): MetricDefinitionKeys {
    switch (metric) {
        case BillingStatus.BILLED:
            return {
                label: 'metric__billed__label',
                definition: 'metric__billed__definition',
                dateBasis: 'metric__billed__date_basis',
            };
        case BillingStatus.BILLED_WO_CREDIT_NOTE:
            return {
                label: 'metric__billed_wo_credit_note__label',
                definition: 'metric__billed_wo_credit_note__definition',
                dateBasis: 'metric__billed_wo_credit_note__date_basis',
            };
        case BillingStatus.NOT_BILLED:
            return {
                label: 'metric__not_billed__label',
                definition: 'metric__not_billed__definition',
                dateBasis: 'metric__not_billed__date_basis',
            };
        case BillingStatus.ORDER_SUM:
            return {
                label: 'metric__order_sum__label',
                definition: 'metric__order_sum__definition',
                documentTypes: 'metric__order_sum__document_types',
                dateBasis: 'metric__order_sum__date_basis',
            };
        case 'GROSS_MARGIN':
            return {
                label: 'metric__gross_margin__label',
                definition: 'metric__gross_margin__definition',
            };
        case 'GROSS_MARGIN_PERCENTAGE':
            return {
                label: 'metric__gross_margin_percentage__label',
                definition: 'metric__gross_margin_percentage__definition',
            };
        case 'UNITS':
            return {
                label: 'metric__units__label',
                definition: 'metric__units__definition',
            };
    }
}

/**
 * i18n key for one ERP document type. `metric__order_sum__document_types` names
 * the document types the tenant counts, and that set is per tenant
 * (`AppConfig.orderSumDocumentTypes`): MEGA counts returns, contracts and memo
 * requests on top of plain orders, most tenants count orders only.
 */
export function documentTypeKey(documentType: SrSalesOrderType): string {
    switch (documentType) {
        case 'ORDER':
            return 'document_type__ORDER';
        case 'CREDITNOTE':
            return 'document_type__CREDITNOTE';
        case 'QUOTE':
            return 'document_type__QUOTE';
        case 'RETURN':
            return 'document_type__RETURN';
        case 'CONTRACT':
            return 'document_type__CONTRACT';
        case 'DEBIT_MEMO_REQUEST':
            return 'document_type__DEBIT_MEMO_REQUEST';
        case 'CREDIT_MEMO_REQUEST':
            return 'document_type__CREDIT_MEMO_REQUEST';
    }
}
